"use client";

import { ArrowRight, Minus, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CommonQuestions() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: faqRef,
    isVisible: faqVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const [openItem, setOpenItem] = useState<string>("item-0");
  const faqs = [{
    question: "How do I register my family for your programs?",
    answer: "Registration is simple and family-centered. You can start by filling out our intake form on the Connect page. Once received, a member of our team will reach out to discuss your family's unique needs, ensure we are a great fit, and guide you through our enrollment process for respite care and inclusive activities."
  }, {
    question: "What kind of support do you offer for caregivers?",
    answer: "We believe caregivers are the heart of our community. We provide respite care services, support groups, and educational workshops designed to give you the space to recharge. Our goal is to ensure you feel empowered, heard, and supported as you navigate your journey."
  }, {
    question: "Are your programs accessible for neurodiverse children?",
    answer: "Absolutely. All our inclusive programs are specifically designed with neurodiverse needs in mind. We prioritize sensory-friendly environments, flexible participation, and trained staff who understand how to create a welcoming, safe, and joyful space for every child to thrive."
  }, {
    question: "Do you provide resources in languages other than English?",
    answer: "Yes, we are committed to being a bridge for all families. We offer bilingual resources and outreach to ensure that language is never a barrier to accessing the support, advocacy, and community connections your family deserves."
  }, {
    question: "How can I get involved or support your mission?",
    answer: "We love our community partners! Whether you are interested in volunteering at our family-centered events, becoming a corporate sponsor, or simply spreading the word about our advocacy efforts, there are many ways to help us light the way for families. Visit our Connect page to learn more."
  }, {
    question: "What is the philosophy behind your community events?",
    answer: "Our events are built on the belief that every family deserves to celebrate and connect without judgment. We create festive, inclusive spaces where families can build friendships, share experiences, and feel the strength of a community that truly understands and celebrates them."
  }];
  return <section data-section-id="388" id="common-questions" className="bg-background text-foreground py-24 md:py-36 px-6 md:px-10 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "64px 64px"
    }} />

      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={headerRef} className={`lg:col-span-5 lg:sticky lg:top-24 self-start transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-8" style={{
              backgroundColor: "var(--primary)"
            }} />
              <span className="font-default text-xs tracking-widest uppercase" style={{
              color: "var(--primary)"
            }}>
                Support & Connection
              </span>
            </div>

            <h2 className="font-default text-4xl md:text-5xl lg:text-[3.5rem] font-semibold leading-[1.05] tracking-tight mb-8">
              Common questions from our families
            </h2>

            <p className="text-[17px] leading-relaxed text-muted-foreground mb-12 max-w-md">
              We are here to help. Find answers to the most common questions about our respite care, advocacy, and community programs at Hope Torchlighters.
            </p>

            <div className="border-t pt-8 max-w-md" style={{
            borderColor: "var(--border)"
          }}>
              <div className="font-default text-xs tracking-widest uppercase text-muted-foreground mb-3">
                [ GET IN TOUCH ]
              </div>
              <p className="font-default text-lg font-medium mb-4">
                Still have questions?
              </p>
              <Link to="/ConnectPage" className="inline-block">
                <Button variant="ghost" className="px-0 h-auto font-default text-xs tracking-widest uppercase hover:bg-transparent group" style={{
                color: "var(--primary)"
              }}>
                  CONTACT US
                  <ArrowRight className="ml-2 size-3.5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>

            {}
            <div className="hidden lg:flex items-center gap-4 mt-16 font-default text-[10px] tracking-widest text-muted-foreground/60">
              <span>COMMUNITY</span>
              <span>·</span>
              <span>ADVOCACY</span>
              <span>·</span>
              <span>SUPPORT</span>
            </div>
          </div>

          {}
          <div ref={faqRef} className="lg:col-span-7">
            <Accordion type="single" collapsible value={openItem} onValueChange={v => setOpenItem(v)} className="space-y-4">
              {faqs.map((faq, i) => {
              const value = `item-${i}`;
              const isOpen = openItem === value;
              const questionNumber = `[Q.${String(i + 1).padStart(2, "0")}]`;
              return <AccordionItem key={i} data-index={i} value={value} className={`group relative border rounded-lg px-7 py-2 transition-all duration-300 last:border-b ${faqVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
                borderColor: isOpen ? "var(--primary)" : "var(--border)",
                backgroundColor: isOpen ? "var(--card)" : "var(--card)",
                boxShadow: isOpen ? "var(--shadow-lg)" : "var(--shadow-sm)",
                transitionDelay: `${i * 60}ms`
              }}>
                    {}
                    {isOpen && <div className="absolute top-0 left-8 right-8 h-px pointer-events-none" style={{
                  background: "linear-gradient(90deg, transparent, var(--primary), transparent)"
                }} />}

                    <AccordionTrigger className="hover:no-underline py-6 [&>svg]:hidden">
                      <div className="flex items-center gap-5 flex-1 text-left">
                        <span className="font-default text-xs tracking-widest uppercase shrink-0 hidden sm:inline-block" style={{
                      color: "var(--primary)"
                    }}>
                          {questionNumber}
                        </span>
                        <span className="font-default text-base md:text-lg font-medium text-foreground leading-snug">
                          {faq.question}
                        </span>
                      </div>
                      <span className="ml-4 shrink-0 flex items-center justify-center size-8 rounded-lg border transition-all duration-300" style={{
                    borderColor: isOpen ? "var(--primary)" : "var(--border)",
                    color: isOpen ? "var(--primary)" : "var(--muted-foreground)",
                    transform: isOpen ? "rotate(45deg)" : "rotate(0deg)"
                  }}>
                        {isOpen ? <Minus className="size-4" strokeWidth={1.5} /> : <Plus className="size-4" strokeWidth={1.5} />}
                      </span>
                    </AccordionTrigger>

                    <AccordionContent className="pb-6">
                      <div className="sm:pl-[5.25rem]">
                        <p className="font-default text-base text-muted-foreground max-w-2xl" style={{
                      lineHeight: 1.65
                    }}>
                          {faq.answer}
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}
            </Accordion>

            {}
            <div className="mt-12 flex justify-center">
              <Link to="/ConnectPage">
                <Button variant="ghost" className="font-default text-xs tracking-widest uppercase hover:bg-transparent group px-0" style={{
                color: "var(--primary)"
              }}>
                  ASK A NEW QUESTION
                  <ArrowRight className="ml-2 size-3.5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}